<template>
  <div>
    <div v-if="showPros" class="pros-horizontal" :class="{ 'pros-optimization': shoppableRedesignOptimization }">
      <ProsCollection :recommendation-config="recommendationConfig" :panel-config="getProsConfig()" @data-load-status-event="loader" />
    </div>
  </div>
</template>

<script setup>
import { computed, toValue } from 'vue';
import ProsCollection from '@nuxt-common/pros-collection/app/components/ProsCollection.vue';
import prosCollectionComponentType from '@nuxt-common/pros-collection/app/utils/prosCollectionComponentType';
import { usePageData } from '~/composables/usePageData';
import { useBrand } from '~/composables/useBrand';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import useColor from '~/modules/color/composables/useColor';
import useProsLoader from '~/composables/utils/useProsLoader';
import { useProductType } from '~/composables/useProductType';
import useUpc from '~/modules/upc/composables/useUpc';
import parseFacetsFromUrl from '~/utils/parseUrl';

const { isBcom } = useBrand();
const { prosInNewTab, shoppableRedesignOptimization, isShopTheSetExpEnabled } = useFeatureEligibility();

const { isMaster } = useProductType();

const { taxonomy } = useCommonProductGetters();
const categoryId = toValue(taxonomy)?.defaultCategoryId;

const { productId, product, killswitches } = usePageData();
const { detail, traits, imagery } = product.product;
const selectedMemberId = useSelectedMemberId();
const { selectedColor } = useColor();
const { selectedUpcId } = useUpc();
const { showPros, loader } = useProsLoader();

const prosAExp = !isMaster || isShopTheSetExpEnabled;
const numberOfItemsForProsAEpx = {
  mediumNumbers: isBcom ? 5.5 : 5.3,
  largeNumbers: 6,
};

const selectedSize = computed(() => {
  const productSizeAttributes = product?.product?.relationships?.upcs?.[selectedUpcId.value]?.attributes;

  return productSizeAttributes?.reduce((acc, { name, value }) => `${acc};${name}:${value}`, '').slice(1);
});

const recommendationConfig = computed(() => ({
  zone: 'PDP_ZONE_A',
  productId,
  memberProductId: selectedMemberId.value,
  categoryId,
  selectedColor: selectedColor.value?.name,
  selectedColorFamily: selectedColor.value?.normalName,
  selectedSize: selectedSize.value,
  ...parseFacetsFromUrl(),
}));

const baseConfig = {
  openInNewTab: prosInNewTab,
  properties: killswitches,
  meta: {
    product: {
      id: productId,
      detail,
      traits,
      imagery,
    },
  },
};

const getProsConfig = () => ({
  ...baseConfig,
  componentType: prosCollectionComponentType.PROS_CAROUSEL,
  numberOfItems: {
    small: isBcom ? 2.4 : 2.2,
    medium: prosAExp ? numberOfItemsForProsAEpx.mediumNumbers : 3,
    large: prosAExp ? numberOfItemsForProsAEpx.largeNumbers : 4,
  },
  customHeaderType: 'div',
  customStyleClass: shoppableRedesignOptimization && isBcom ? 'shoppable-custom-zone-ag-styling' : 'custom-zone-ag-styling',
  customRole: 'heading',
  customAriaLevel: shoppableRedesignOptimization && isBcom ? '3' : '4',
});
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@include breakpoint(large) {
  .prosAWrapper {
    min-width: rem-calc(102);
    width: rem-calc(102);
  }
}
.pros-horizontal.pros-optimization {
  :deep(.carousel-container) {
    .title-container {
      @include margin-bottom-s;
    }
  }
}
.custom-zone-ag-styling {
  @include breakpoint(small only) {
    :deep(.header-container) {
      div {
        @include header(h4);
      }
    }
  }

  :deep(.header-container) {
    div {
      @include header(h4);
    }
  }
}
.shoppable-custom-zone-ag-styling {
  @include breakpoint(small only) {
    :deep(.header-container) {
      div {
        @include header(h4);
      }
    }
  }

  :deep(.header-container) {
    div {
      @include header(h3);
    }
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@include breakpoint(medium) {
  .pros-horizontal {
    :deep(.brand-and-name) {
      :nth-child(2) {
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        line-clamp: 1;
      }
    }
    :deep(.product-brand) {
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      line-clamp: 1;
    }
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
@include breakpoint(medium) {
  .pros-horizontal {
    :deep(.product-brand) {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
}
</style>
